/**
 * lib/api/identity/auth.ts
 * Split out of lib/api.ts (mechanical refactor, zero behavior change) —
 * moved verbatim; see lib/api.ts for the barrel that re-exports this file.
 */
import { request, ApiError } from '../client.js';
import type {
  AuthMeResponse,
  AuthSessionResponse,
  LoginResponse,
  SelectCompanyBody,
  SwitchCompanyBody,
} from '@suite/contracts-kernel';

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface LoginCredentials {
  email: string;
  password: string;
  /** Optional disambiguator for multi-membership accounts. Never sets tenantId directly. */
  companySlug?: string;
}

/** GET /auth/providers — which external sign-in options the login screen offers. */
export type SsoProvider = 'microsoft' | 'google';

export function getAuthProviders(): Promise<Partial<Record<SsoProvider, boolean>>> {
  return request<Partial<Record<SsoProvider, boolean>>>('/auth/providers');
}

/** Full-page navigation target for "Ingresar con <provider>" (the API redirects to the provider). */
export function ssoLoginUrl(provider: SsoProvider): string {
  const API_BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(/\/+$/, '');
  return `${API_BASE_URL}/auth/${provider}/start`;
}

/** GET /auth/me — returns company + user + memberships for the authenticated tenant user. */
export function getMe(): Promise<AuthMeResponse> {
  return request<AuthMeResponse>('/auth/me');
}

/**
 * GET /auth/session — the SPA's identity source. Returns the verified claims
 * (scope, roles, capabilities, exp) authenticated by the httpOnly cookie; NEVER
 * the token. Throws ApiError(401) when there is no valid session.
 */
export function getSession(): Promise<AuthSessionResponse> {
  return request<AuthSessionResponse>('/auth/session');
}

/**
 * POST /auth/refresh — silent session renewal (session bug fix, 2026-07-21).
 * Re-mints a fresh token (same claims, new exp) and re-sets the httpOnly
 * cookie server-side; returns the same claims shape as GET /auth/session so
 * the caller can update in-memory state directly. Called proactively by
 * session.ts's refresh timer WHILE the current token is still valid — throws
 * ApiError(401) if the current session already expired (never revives a dead
 * session).
 */
export function refreshSession(): Promise<AuthSessionResponse> {
  return request<AuthSessionResponse>('/auth/refresh', { method: 'POST' });
}

/** POST /auth/logout — clears the httpOnly session cookie server-side. */
export function logout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' });
}

/**
 * POST /auth/login — public; returns a discriminated union:
 *   { kind: 'token', token }           → single-membership or slug-matched: go to dashboard
 *   { kind: 'choose-company', ... }    → multi-membership: show CompanyPicker step
 *
 * KI-24.6 / design §9.1
 */
export function login(credentials: LoginCredentials): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: credentials,
    anonymous: true,
  });
}

/**
 * POST /auth/select-company — exchange an account-session token + companyId for a tenant token.
 * The account-session token (from a choose-company response) is passed as the Bearer header.
 * KI-27 / design §9.2
 */
export function selectCompany(
  body: SelectCompanyBody,
  accountSession: string,
): Promise<{ token: string }> {
  // The account-session is a transient token (in memory, not the app session): we
  // send it explicitly as Bearer to authenticate this single call. credentials:'include'
  // lets the browser STORE the suite_session cookie that the response sets.
  const API_BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(/\/+$/, '');
  return fetch(`${API_BASE_URL}/auth/select-company`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accountSession}`,
    },
    body: JSON.stringify(body),
  }).then(async (res) => {
    if (!res.ok) {
      let code = 'UNKNOWN';
      let message = `Request failed with status ${res.status}`;
      try {
        const envelope = (await res.json()) as Partial<{ code: string; message: string }>;
        if (typeof envelope.code === 'string') code = envelope.code;
        if (typeof envelope.message === 'string') message = envelope.message;
      } catch { /* non-JSON body */ }
      throw new ApiError(res.status, code, message);
    }
    return res.json() as Promise<{ token: string }>;
  });
}

/**
 * POST /auth/switch-company — exchange current tenant token + targetCompanyId for a new tenant token.
 * Uses the current tenant token (getToken()) as the Bearer.
 * KI-28 / design §9.3
 */
export function switchCompany(body: SwitchCompanyBody): Promise<{ token: string }> {
  return request<{ token: string }>('/auth/switch-company', {
    method: 'POST',
    body,
  });
}
